import { useEffect, useState, useRef } from "react";
import {
  adminUsuarios, adminCrearUsuario, adminActualizarUsuario, adminAsignacion, adminAsignar,
  adminVehiculos, adminUpsertVehiculo, adminVehiculosConfig, adminSetVehiculosConfig,
  adminImportVehiculos, adminParametros, adminSetParametro, adminAuditoria, formatErr,
} from "@/lib/staffApi";
import { useStaff } from "@/pages/staff/StaffApp";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const TABS = [
  { key: "usuarios", label: "Usuarios" },
  { key: "asignacion", label: "Asignación" },
  { key: "vehiculos", label: "Vehículos" },
  { key: "parametros", label: "Parámetros" },
  { key: "auditoria", label: "Auditoría" },
];

function Guarded({ loader, children, testid }) {
  const [state, setState] = useState({ loading: true, denied: false, data: null });
  const reload = () =>
    loader()
      .then((d) => setState({ loading: false, denied: false, data: d }))
      .catch((e) => setState({ loading: false, denied: e.response?.status === 403, data: null }));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  if (state.loading) return <div className="flex items-center gap-2 text-neutral-400"><Loader2 className="animate-spin" /> Cargando…</div>;
  if (state.denied) return <div className="text-amber-400 text-sm" data-testid={`${testid}-denied`}>No tienes permiso para este módulo.</div>;
  return children(state.data, reload);
}

export default function AdminPanel() {
  const { user } = useStaff();
  const [tab, setTab] = useState("usuarios");

  return (
    <div data-testid="admin-panel">
      <h1 className="text-2xl font-bold mb-1">Administración</h1>
      <p className="text-sm text-neutral-500 mb-6">Sesión: {user.name} ({user.role})</p>
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
            className={`px-4 py-2 rounded-lg text-sm ${tab === t.key ? "bg-orange-500 text-black font-semibold" : "bg-[#111] border border-neutral-800 text-neutral-400"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "usuarios" && (
        <Guarded loader={adminUsuarios} testid="admin-usuarios">
          {(usuarios, reload) => <Usuarios usuarios={usuarios} reload={reload} />}
        </Guarded>
      )}
      {tab === "asignacion" && (
        <Guarded loader={adminAsignacion} testid="admin-asignacion">
          {(tramites, reload) => <Asignacion tramites={tramites} reload={reload} />}
        </Guarded>
      )}
      {tab === "vehiculos" && (
        <Guarded loader={adminVehiculos} testid="admin-vehiculos">
          {(vehiculos, reload) => <Vehiculos vehiculos={vehiculos} reload={reload} />}
        </Guarded>
      )}
      {tab === "parametros" && (
        <Guarded loader={adminParametros} testid="admin-parametros">
          {(parametros, reload) => <Parametros parametros={parametros} reload={reload} />}
        </Guarded>
      )}
      {tab === "auditoria" && (
        <Guarded loader={adminAuditoria} testid="admin-auditoria">
          {(items) => <Auditoria items={items} />}
        </Guarded>
      )}
    </div>
  );
}

function Usuarios({ usuarios, reload }) {
  const [form, setForm] = useState({ name: "", email: "", role: "asesor" });
  const crear = async () => {
    try {
      const r = await adminCrearUsuario(form);
      toast.success(`Usuario creado. Contraseña temporal: ${r.temp_password}`);
      setForm({ name: "", email: "", role: "asesor" });
      reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const toggle = async (u) => {
    try { await adminActualizarUsuario(u.id, { activo: !u.activo }); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-usuarios-panel">
      <div className="bg-[#111] border border-neutral-800 rounded-xl p-4 grid grid-cols-4 gap-3">
        <input placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="user-name" className="bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2 text-sm" />
        <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="user-email" className="bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2 text-sm" />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
          data-testid="user-role" className="bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2 text-sm">
          <option value="asesor">Asesor</option>
          <option value="mantenimiento">Mantenimiento</option>
          <option value="administrador">Administrador</option>
        </select>
        <button onClick={crear} data-testid="user-create" className="bg-orange-500 text-black font-semibold rounded-lg text-sm">Crear</button>
      </div>
      <div className="bg-[#111] border border-neutral-800 rounded-xl divide-y divide-neutral-800">
        {usuarios.map((u) => (
          <div key={u.id} className="flex items-center justify-between px-4 py-3 text-sm">
            <div><span className="font-medium">{u.name}</span> · {u.email} · <span className="text-neutral-500">{u.role}</span></div>
            {u.role !== "ceo" && (
              <button onClick={() => toggle(u)} className={`text-xs px-3 py-1 rounded ${u.activo ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                {u.activo ? "Activo" : "Inactivo"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Asignacion({ tramites, reload }) {
  const asignar = async (tid) => {
    const asesorId = prompt("ID del asesor destino:");
    if (!asesorId) return;
    try { await adminAsignar(tid, asesorId); toast.success("Reasignado"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-[#111] border border-neutral-800 rounded-xl divide-y divide-neutral-800" data-testid="admin-asignacion-panel">
      {tramites.map((t) => (
        <div key={t.tramite_id} className="flex items-center justify-between px-4 py-3 text-sm">
          <div>{t.folio} · {t.nombre} · {t.modalidad} · <span className="text-neutral-500">{t.asignado_nombre || "Sin asignar"}</span></div>
          <button onClick={() => asignar(t.tramite_id)} className="text-xs bg-neutral-800 px-3 py-1 rounded">Reasignar</button>
        </div>
      ))}
    </div>
  );
}

const VEH_FIELDS = [
  "ID_VEHICULO", "MARCA", "MODELO", "VERSION", "ANIO", "CATEGORIA", "CONDICION",
  "MODALIDADES", "PRECIO_LISTA", "PRECIO_AUTOFINANZA", "MENSUALIDAD_DESDE",
  "ENGANCHE_DESDE", "TASA_DESDE", "CAT", "INVENTARIO", "PROMOCION", "VIGENCIA_DESDE",
  "VIGENCIA_HASTA", "ID_OFERTA", "URL_QR", "IMAGEN", "ESTATUS_VALIDACION", "ACTIVO",
  "FUENTE", "DESTACADO",
];
const isSi = (x) => String(x).trim().toLowerCase() === "si" || String(x).trim().toLowerCase() === "sí";

function VehiculoForm({ initial, onClose, reload }) {
  const editing = !!(initial && initial.ID_VEHICULO);
  const [f, setF] = useState(() => {
    const base = {}; VEH_FIELDS.forEach((k) => { base[k] = initial?.[k] ?? ""; });
    if (!editing) { base.ACTIVO = base.ACTIVO || "SI"; base.DESTACADO = base.DESTACADO || "NO"; }
    return base;
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!editing && !String(f.ID_VEHICULO || "").trim() && f.ID_VEHICULO !== "") { /* backend genera si vacío */ }
    setSaving(true);
    try {
      const payload = {}; VEH_FIELDS.forEach((k) => { if (f[k] !== "" && f[k] != null) payload[k] = f[k]; });
      if (!editing && !String(payload.ID_VEHICULO || "").trim()) delete payload.ID_VEHICULO; // deja que backend genere
      await adminUpsertVehiculo(payload);
      toast.success(editing ? "Vehículo actualizado" : "Vehículo agregado");
      onClose(); reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 overflow-auto py-8" data-testid="vehiculo-form-modal">
      <div className="bg-[#111] border border-neutral-800 rounded-xl w-full max-w-3xl mx-4 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">{editing ? "Editar vehículo" : "Agregar vehículo"}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white text-sm" data-testid="vehiculo-form-close">Cerrar</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {VEH_FIELDS.map((k) => (
            <label key={k} className="text-xs text-neutral-400 flex flex-col gap-1">
              {k}
              <input
                data-testid={`veh-field-${k}`}
                className="bg-[#0d0d0d] border border-neutral-800 rounded px-2 py-1.5 text-sm text-white disabled:opacity-50"
                value={f[k] ?? ""}
                disabled={editing && k === "ID_VEHICULO"}
                onChange={(e) => set(k, e.target.value)}
                placeholder={k === "ID_VEHICULO" && !editing ? "(auto si se deja vacío)" : ""}
              />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-neutral-300 border border-neutral-800">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-4 py-1.5 rounded text-sm bg-orange-600 text-white disabled:opacity-50" data-testid="veh-form-save">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CatalogoConfig() {
  const [cfg, setCfg] = useState(null);
  const [custom, setCustom] = useState(8);
  useEffect(() => {
    adminVehiculosConfig().then((c) => { setCfg(c); if (c.modo === "LIMIT" && c.cantidad) setCustom(c.cantidad); }).catch(() => setCfg({ modo: "LIMIT", cantidad: 8 }));
  }, []);
  if (!cfg) return <div className="text-neutral-500 text-sm">Cargando configuración…</div>;
  const isAll = cfg.modo === "ALL";
  const is8 = cfg.modo === "LIMIT" && Number(cfg.cantidad) === 8;
  const isCustom = cfg.modo === "LIMIT" && Number(cfg.cantidad) !== 8;
  const save = async (modo, cantidad) => {
    try { const r = await adminSetVehiculosConfig(modo, cantidad); setCfg(r); toast.success("Configuración guardada"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-[#111] border border-neutral-800 rounded-xl p-4 space-y-3" data-testid="vehiculos-config">
      <div className="text-white text-sm font-medium">Número de vehículos mostrados en el catálogo público</div>
      <div className="flex flex-wrap gap-2 items-center">
        <button data-testid="cfg-8" onClick={() => save("LIMIT", 8)}
          className={`px-3 py-1.5 rounded text-sm border ${is8 ? "bg-orange-600 text-white border-orange-600" : "text-neutral-300 border-neutral-800"}`}>8 vehículos</button>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm border ${isCustom ? "border-orange-600" : "border-neutral-800"}`}>
          <span className="text-neutral-300">Personalizado</span>
          <input data-testid="cfg-custom-input" type="number" min="1" value={custom}
            onChange={(e) => setCustom(e.target.value)}
            className="w-16 bg-[#0d0d0d] border border-neutral-800 rounded px-2 py-1 text-white" />
          <button data-testid="cfg-custom-save" onClick={() => save("LIMIT", parseInt(custom, 10))}
            className="px-2 py-1 rounded text-xs bg-neutral-800 text-white">Guardar</button>
        </div>
        <button data-testid="cfg-all" onClick={() => save("ALL", null)}
          className={`px-3 py-1.5 rounded text-sm border ${isAll ? "bg-orange-600 text-white border-orange-600" : "text-neutral-300 border-neutral-800"}`}>Todos los vehículos activos</button>
      </div>
      <div className="text-xs text-neutral-500">Actual: {isAll ? "Todos los activos" : `${cfg.cantidad} vehículos`}</div>
    </div>
  );
}

function CargaMasiva({ reload }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const importar = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Selecciona un archivo Excel (.xlsx)");
    setBusy(true); setRes(null);
    try {
      const r = await adminImportVehiculos(file);
      setRes(r);
      toast.success(`${r.creados} creados · ${r.actualizados} actualizados · ${r.errores?.length || 0} errores`);
      reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#111] border border-neutral-800 rounded-xl p-4 space-y-3" data-testid="vehiculos-carga-masiva">
      <div className="text-white text-sm font-medium">Carga masiva de vehículos</div>
      <div className="text-xs text-neutral-500">Archivo .xlsx con hoja obligatoria <b>VEHICULOS</b>. Actualiza/crea por <b>ID_VEHICULO</b>. No elimina vehículos ausentes.</div>
      <div className="flex flex-wrap gap-2 items-center">
        <input ref={fileRef} type="file" accept=".xlsx" data-testid="veh-import-file"
          className="text-sm text-neutral-300 file:mr-2 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-neutral-800 file:text-white" />
        <button onClick={importar} disabled={busy} data-testid="veh-import-btn"
          className="px-4 py-1.5 rounded text-sm bg-orange-600 text-white disabled:opacity-50">
          {busy ? "Importando…" : "Importar vehículos"}
        </button>
      </div>
      {res && (
        <div className="text-sm text-neutral-300 space-y-2" data-testid="veh-import-result">
          <div>{res.creados} creados · {res.actualizados} actualizados · {res.errores?.length || 0} errores · {res.total} total</div>
          {res.columnas_ignoradas?.length > 0 && (
            <div className="text-xs text-amber-400">Columnas ignoradas: {res.columnas_ignoradas.join(", ")}</div>
          )}
          {res.errores?.length > 0 && (
            <div className="max-h-48 overflow-auto border border-neutral-800 rounded">
              <table className="w-full text-xs">
                <thead className="text-neutral-500"><tr><th className="text-left px-2 py-1">Fila</th><th className="text-left px-2 py-1">ID_VEHICULO</th><th className="text-left px-2 py-1">Motivo</th></tr></thead>
                <tbody>
                  {res.errores.map((er, i) => (
                    <tr key={i} className="border-t border-neutral-800"><td className="px-2 py-1">{er.fila}</td><td className="px-2 py-1">{er.ID_VEHICULO || "—"}</td><td className="px-2 py-1 text-red-400">{er.motivo}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Vehiculos({ vehiculos, reload }) {
  const [form, setForm] = useState(null); // null | {} (nuevo) | vehiculo (editar)
  const toggle = async (v, field) => {
    try {
      const cur = isSi(v[field]);
      await adminUpsertVehiculo({ ID_VEHICULO: v.ID_VEHICULO, [field]: cur ? "NO" : "SI" });
      toast.success(`${field} actualizado`); reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5" data-testid="admin-vehiculos-panel">
      {/* A) CATÁLOGO */}
      <div className="flex items-center justify-between">
        <h3 className="text-white text-sm font-medium">Catálogo de vehículos</h3>
        <button onClick={() => setForm({})} data-testid="veh-add-btn" className="px-3 py-1.5 rounded text-sm bg-orange-600 text-white">+ Agregar vehículo</button>
      </div>
      <div className="bg-[#111] border border-neutral-800 rounded-xl divide-y divide-neutral-800">
        {vehiculos.map((v) => (
          <div key={v.ID_VEHICULO} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid={`veh-row-${v.ID_VEHICULO}`}>
            <div className="min-w-0">
              <div className="text-white truncate">{v.MARCA} {v.MODELO} {v.VERSION} · {v.ANIO} · <span className="text-neutral-500">{v.CATEGORIA}</span></div>
              <div className="text-xs text-neutral-500">{v.ID_VEHICULO}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded ${isSi(v.ACTIVO) ? "bg-emerald-500/15 text-emerald-400" : "bg-neutral-800 text-neutral-500"}`}>{isSi(v.ACTIVO) ? "ACTIVO" : "INACTIVO"}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${isSi(v.DESTACADO) ? "bg-orange-500/15 text-orange-400" : "bg-neutral-800 text-neutral-500"}`}>{isSi(v.DESTACADO) ? "★ Destacado" : "No destacado"}</span>
              <button onClick={() => toggle(v, "ACTIVO")} data-testid={`veh-toggle-activo-${v.ID_VEHICULO}`} className="px-2 py-1 rounded text-xs border border-neutral-800 text-neutral-300">{isSi(v.ACTIVO) ? "Desactivar" : "Activar"}</button>
              <button onClick={() => toggle(v, "DESTACADO")} data-testid={`veh-toggle-destacado-${v.ID_VEHICULO}`} className="px-2 py-1 rounded text-xs border border-neutral-800 text-neutral-300">{isSi(v.DESTACADO) ? "Quitar destacado" : "Destacar"}</button>
              <button onClick={() => setForm(v)} data-testid={`veh-edit-${v.ID_VEHICULO}`} className="px-2 py-1 rounded text-xs bg-neutral-800 text-white">Editar</button>
            </div>
          </div>
        ))}
        {vehiculos.length === 0 && <div className="px-4 py-6 text-neutral-500 text-sm">No hay vehículos.</div>}
      </div>

      {/* B) CONFIGURACIÓN DEL CATÁLOGO PÚBLICO */}
      <CatalogoConfig />

      {/* C) CARGA MASIVA */}
      <CargaMasiva reload={reload} />

      {form !== null && <VehiculoForm initial={form} onClose={() => setForm(null)} reload={reload} />}
    </div>
  );
}

function Parametros({ parametros, reload }) {
  const editable = parametros.filter((p) => ["Minutos", "Número"].includes(String(p.TIPO)));
  const set = async (p) => {
    const v = prompt(`Nuevo valor para ${p.CLAVE}:`, p.VALOR);
    if (v == null) return;
    try { await adminSetParametro(p.ID_PARAMETRO, v); toast.success("Parámetro actualizado"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-[#111] border border-neutral-800 rounded-xl divide-y divide-neutral-800" data-testid="admin-parametros-panel">
      {editable.map((p) => (
        <div key={p.ID_PARAMETRO} className="flex items-center justify-between px-4 py-3 text-sm">
          <div>{p.CLAVE} = <span className="font-mono text-orange-400">{p.VALOR}</span> <span className="text-neutral-600 text-xs">({p.TIPO})</span>
            {String(p.ESTATUS) === "PENDIENTE" && <span className="text-amber-400 text-xs ml-2">requiere validación comercial</span>}
          </div>
          {String(p.ESTATUS) !== "PENDIENTE" && <button onClick={() => set(p)} className="text-xs bg-neutral-800 px-3 py-1 rounded">Editar</button>}
        </div>
      ))}
    </div>
  );
}

function Auditoria({ items }) {
  return (
    <div className="bg-[#111] border border-neutral-800 rounded-xl divide-y divide-neutral-800 text-sm" data-testid="admin-auditoria-panel">
      {items.map((a) => (
        <div key={a.id} className="px-4 py-3">
          <span className="text-orange-400">{a.accion}</span> · {a.recurso} · <span className="text-neutral-500">{a.usuario_email || a.rol}</span>
          <span className="text-xs text-neutral-600 ml-2">{a.ts}</span>
        </div>
      ))}
    </div>
  );
}
